// Shared helpers for turning logbookentry `completed`/`ins_so` timestamps into
// chart-ready trend buckets at day/week/month granularity. Used by both the
// aggregate "Sign-offs over time" chart (ExaminerDashboard.vue) and the
// per-logbook drill-down chart (LogbookSignOffModal.vue).

// Nullable datetime strings from the API arrive in more than one shape
// (MySQL "YYYY-MM-DD HH:mm:ss" or a display "DD/MM/YYYY[ HH:mm:ss]" format) —
// mirrors the dual-parse precedent in Instructor/MultiRevokeSignOffModal.vue.
export function parseSignOffDate(raw) {
  if (!raw) return null;

  // Year-first strings aren't ambiguous, so native parsing is safe here.
  // Day-first "DD/MM/YYYY" strings are NOT safe to hand to `new Date()`
  // first: it assumes MM/DD/YYYY for slash-separated dates, so it silently
  // returns the *wrong* date whenever the day is <=12 (e.g. "05/01/2026"
  // becomes May 1st, not Jan 5th) instead of failing - it can only be
  // trusted once the year-first shape has been confirmed up front.
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const iso = new Date(raw);
    return isNaN(iso.getTime()) ? null : iso;
  }

  const [datePart, timePart] = String(raw).split(' ');
  if (!datePart) return null;
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours = 0, minutes = 0, seconds = 0] = (timePart || '').split(':').map(Number);
  if (!day || !month || !year) return null;
  const parsed = new Date(year, month - 1, day, hours, minutes, seconds);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function pad(n) {
  return String(n).padStart(2, '0');
}

function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfWeek(date) {
  const d = startOfDay(date);
  const day = d.getDay(); // 0 (Sun) .. 6 (Sat)
  const diff = (day === 0 ? -6 : 1) - day; // shift back to Monday
  d.setDate(d.getDate() + diff);
  return d;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function dayKey(date) {
  const d = startOfDay(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function weekKey(date) {
  const d = startOfWeek(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function monthKey(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

const DAY_LABEL = new Intl.DateTimeFormat('en-GB', { month: 'short', day: 'numeric' });
const MONTH_LABEL = new Intl.DateTimeFormat('en-GB', { month: 'short', year: 'numeric' });

// `bucketSpan` returns the exclusive end of the period a bucket covers, given
// its (inclusive) start - used to build each bucket's [start, end) date range
// for click-to-drill-down filtering.
const GRANULARITY_CONFIG = {
  day: { startFn: startOfDay, keyFn: dayKey, labelFormatter: DAY_LABEL, advance: (d) => d.setDate(d.getDate() + 1), maxBuckets: 60, bucketSpan: (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1) },
  week: { startFn: startOfWeek, keyFn: weekKey, labelFormatter: DAY_LABEL, advance: (d) => d.setDate(d.getDate() + 7), maxBuckets: 26, bucketSpan: (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7) },
  month: { startFn: startOfMonth, keyFn: monthKey, labelFormatter: MONTH_LABEL, advance: (d) => d.setMonth(d.getMonth() + 1), maxBuckets: 24, bucketSpan: (d) => new Date(d.getFullYear(), d.getMonth() + 1, 1) },
};

export const GRANULARITY_OPTIONS = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

/**
 * Buckets a flat list of { date, seriesKey } records into gap-filled,
 * chronologically-ordered buckets at the given granularity.
 *
 * @param {{ date: Date, seriesKey: string }[]} records
 * @param {'day'|'week'|'month'} granularity
 * @returns {{ buckets: Array<{ label: string, [seriesKey: string]: number }>, truncated: boolean }}
 */
export function buildTrend(records, granularity) {
  const cfg = GRANULARITY_CONFIG[granularity] || GRANULARITY_CONFIG.month;
  const bucketMap = new Map();
  let minStart = null;
  let maxStart = null;

  records.forEach(({ date, seriesKey }) => {
    if (!date) return;
    const start = cfg.startFn(date);
    const key = cfg.keyFn(date);
    if (!minStart || start < minStart) minStart = start;
    if (!maxStart || start > maxStart) maxStart = start;
    if (!bucketMap.has(key)) bucketMap.set(key, {});
    const bucket = bucketMap.get(key);
    bucket[seriesKey] = (bucket[seriesKey] || 0) + 1;
  });

  if (!minStart || !maxStart) {
    return { buckets: [], truncated: false };
  }

  // Walk every period between the earliest and latest sign-off so the line
  // doesn't silently skip quiet days/weeks/months.
  const allBuckets = [];
  const cursor = new Date(minStart);
  while (cursor <= maxStart) {
    const key = cfg.keyFn(cursor);
    const counts = bucketMap.get(key) || {};
    const start = new Date(cursor);
    allBuckets.push({ label: cfg.labelFormatter.format(cursor), start, end: cfg.bucketSpan(start), ...counts });
    cfg.advance(cursor);
  }

  const truncated = allBuckets.length > cfg.maxBuckets;
  const buckets = truncated ? allBuckets.slice(-cfg.maxBuckets) : allBuckets;
  return { buckets, truncated };
}

/**
 * Buckets several named date-lists into a "burn-up" shape: for each period,
 * every series' value is the *running total* of its dates up to and
 * including that period, rather than just the count within that period -
 * used for per-trainee cumulative-completion charts, where the interesting
 * question is "how much has this person finished so far", not "how much did
 * they finish this week alone".
 *
 * @param {{ key: string, dates: Date[] }[]} seriesInputs
 * @param {'day'|'week'|'month'} granularity
 * @param {Date} rangeStart - inclusive
 * @param {Date} rangeEnd - inclusive
 * @returns {Array<{ label: string, start: Date, end: Date, [seriesKey: string]: number }>}
 */
export function buildCumulativeTrend(seriesInputs, granularity, rangeStart, rangeEnd) {
  const cfg = GRANULARITY_CONFIG[granularity] || GRANULARITY_CONFIG.month;
  if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) return [];

  const sortedDatesByKey = new Map(
    seriesInputs.map((s) => [s.key, s.dates.filter(Boolean).slice().sort((a, b) => a - b)])
  );
  const runningIndexByKey = new Map(seriesInputs.map((s) => [s.key, 0]));

  const allBuckets = [];
  const cursor = cfg.startFn(new Date(rangeStart));
  const lastStart = cfg.startFn(new Date(rangeEnd));

  while (cursor <= lastStart) {
    const start = new Date(cursor);
    const end = cfg.bucketSpan(start);
    const bucket = { label: cfg.labelFormatter.format(cursor), start, end };

    seriesInputs.forEach(({ key }) => {
      const dates = sortedDatesByKey.get(key);
      let idx = runningIndexByKey.get(key);
      while (idx < dates.length && dates[idx] < end) idx += 1;
      runningIndexByKey.set(key, idx);
      bucket[key] = idx;
    });

    allBuckets.push(bucket);
    cfg.advance(cursor);
  }

  return allBuckets.length > cfg.maxBuckets ? allBuckets.slice(-cfg.maxBuckets) : allBuckets;
}
